import { useState } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

export default function BeforeAfterSlider({
  beforeSrc,
  beforeSrcSet,
  afterSrc,
  afterSrcSet,
  sizes = "100vw",
  alt = "Apercu Éco-Bain",
  imageFit = "contain",
  imagePositionClass = "",
  showAfterLogo = false,
}) {
  const { t } = useLanguage();
  const [position, setPosition] = useState(50);
  const imageFitClass = imageFit === "cover" ? "object-cover" : "object-contain";

  return (
    <div className="relative max-w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-2xl dark:border-white/10 dark:bg-white/5 sm:rounded-3xl">
      <div className="relative aspect-[4/5] w-full sm:aspect-[16/10]">
        <img
          src={afterSrc}
          srcSet={afterSrcSet}
          sizes={sizes}
          alt={`${alt} ${t("aiPreview.slider.after").toLowerCase()}`}
          width="1280"
          height="853"
          loading="lazy"
          decoding="async"
          className={`absolute inset-0 h-full w-full ${imageFitClass} ${imagePositionClass}`}
          draggable="false"
        />
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${position}%` }}
          aria-hidden="true"
        >
          <img
            src={beforeSrc}
            srcSet={beforeSrcSet}
            sizes={sizes}
            alt=""
            width="1280"
            height="853"
            loading="lazy"
            decoding="async"
            className={`h-full w-full ${imageFitClass} ${imagePositionClass}`}
            style={{ width: `${10000 / Math.max(position, 1)}%`, maxWidth: "none" }}
            draggable="false"
          />
        </div>

        <div className="absolute bottom-3 left-3 rounded-full bg-black/55 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
          {t("aiPreview.slider.before")}
        </div>
        <div className="absolute bottom-3 right-3 rounded-full bg-white/85 px-3 py-1 text-xs font-semibold text-slate-800 backdrop-blur">
          {t("aiPreview.slider.after")}
        </div>

        {showAfterLogo && (
          <div
            className="pointer-events-none absolute inset-y-0 right-0 overflow-hidden"
            style={{ width: `${100 - position}%` }}
          >
            <img
              src="/logo.svg"
              alt={t("aiPreview.brandName")}
              width="157"
              height="157"
              loading="lazy"
              className="absolute right-3 top-3 h-auto w-14 opacity-95 dark:hidden sm:right-4 sm:top-4 sm:w-16"
            />
            <img
              src="/logo-light.svg"
              alt={t("aiPreview.brandName")}
              width="157"
              height="157"
              loading="lazy"
              className="absolute right-3 top-3 hidden h-auto w-14 opacity-95 dark:block sm:right-4 sm:top-4 sm:w-16"
            />
          </div>
        )}

        <div
          className="pointer-events-none absolute bottom-0 top-0 w-0.5 bg-white shadow-[0_0_0_1px_rgba(15,23,42,0.2)]"
          style={{ left: `${position}%` }}
        >
          <div className="absolute left-1/2 top-1/2 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-white/80 bg-white/95 text-slate-800 shadow-xl">
            <ChevronsLeftRight size={20} />
          </div>
        </div>
      </div>

      <label className="sr-only" htmlFor="ai-preview-slider">
        {t("aiPreview.slider.label")}
      </label>
      <input
        id="ai-preview-slider"
        type="range"
        min="5"
        max="95"
        value={position}
        onChange={(event) => setPosition(Number(event.target.value))}
        className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
        aria-label={t("aiPreview.slider.label")}
      />
    </div>
  );
}
